import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as Bananas from "bananas-commerce-admin";

import {
  Gavel as GavelIcon,
  Newspaper as NewspaperIcon,
} from "@mui/icons-material";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import { Button } from "@mui/material";

import ErrorCard from "@/extensions/klarahill/components/ErrorCard";

import NewPriceListDialog from "../../components/NewPriceListDialog";
import PriceListCard from "../../components/PriceListCard";
import PublicationsList from "../../components/PublicationsList";
import ScheduleCard from "../../components/ScheduleCard";
import SchedulesActionBar from "../../components/SchedulesActionBar";
import {
  DetailedPublicationSchema,
  PricesGroupSchema,
  PublishingRuleRetrieveSchema,
} from "../../types";

import { Nullish } from "@/utils/types";

export type PublicationTab = "titles" | "schedules" | "prices";

export interface PriceListCardProps {
  data: PricesGroupSchema;
  isNew: boolean;
}

export const PublicationListPage: Bananas.types.PageComponent = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const api = Bananas.useApi();
  const [publications, setPublications] =
    useState<DetailedPublicationSchema[]>();
  const [schedules, setSchedules] = useState<PublishingRuleRetrieveSchema[]>();
  const [prices, setPrices] = useState<PriceListCardProps[]>();
  const [isNewPriceListDialogOpen, setIsNewPriceListDialogOpen] =
    useState(false);
  const activeTab: Nullish<PublicationTab> = searchParams.get(
    "tab",
  ) as PublicationTab;
  const { t } = Bananas.useI18n();
  const hasLoadedRef = useRef<Set<PublicationTab | "default">>(new Set());

  const fetchData = async (id: string) => {
    const operation = api.operations[id];

    if (!operation) {
      throw new Error(`No action found for "${id}"`);
    }
    const response = await operation.call();
    const data = await response.json();
    return data;
  };

  const refresh = async () => {
    if (activeTab === "schedules") {
      const schedulesData = await fetchData("mediahouse.publishing-rule:list");
      setSchedules(schedulesData);
    } else if (activeTab === "prices") {
      const [pricesData, publicationsData] = await Promise.all([
        fetchData("mediahouse.prices:get"),
        fetchData("mediahouse.publications:get"),
      ]);
      setPrices(
        pricesData.map((price: PricesGroupSchema) => ({
          data: price,
          isNew: false,
        })),
      );
      setPublications(publicationsData);
    } else {
      const data = await fetchData("mediahouse.publications:get");
      setPublications(data);
    }
  };

  const handleCreatePriceList = async (fromDate: string, toDate: string) => {
    const newPriceList: PricesGroupSchema = {
      from_date: fromDate,
      to_date: toDate,
      publications_prices: [],
    };
    setPrices([...(prices ?? []), { data: newPriceList, isNew: true }]);
  };

  const handleCopyPriceList = (newPriceList: PricesGroupSchema) => {
    setPrices([...(prices ?? []), { data: newPriceList, isNew: true }]);
  };

  const sortedPrices = useMemo(() => {
    if (!prices) return [];
    return [...prices].sort(
      (a: PriceListCardProps, b: PriceListCardProps) =>
        new Date(a.data.from_date).getTime() -
        new Date(b.data.from_date).getTime(),
    );
  }, [prices]);

  useEffect(() => {
    const currentTab = activeTab || "default";

    if (hasLoadedRef.current.has(currentTab)) return;

    if (activeTab === "schedules" && schedules != null) return;
    if (
      (activeTab === "titles" || activeTab === "prices" || activeTab == null) &&
      publications != null
    )
      return;

    // Mark as loaded and fetch
    hasLoadedRef.current.add(currentTab);
    refresh();
  }, [activeTab]);

  return (
    <Bananas.Page defaultTab="titles" tabSearchParam="tab">
      <Bananas.ContentWrapperWithActionBar>
        <Bananas.Header variant="opaque">
          <Bananas.TitleBar title={t("Utgivning")}></Bananas.TitleBar>

          <Bananas.Tabs
            aria-label={t("Kontrollera vilka begravningsärendent som syns.")}
            defaultValue="titles"
            onChange={(tab) => {
              setSearchParams((sp) => ({
                ...Object.fromEntries(sp.entries()),
                tab,
              }));
            }}
          >
            <Bananas.Tab
              key="default"
              icon={<NewspaperIcon />}
              label={t("Titlar")}
              value="titles"
            />
            <Bananas.Tab
              icon={<GavelIcon />}
              label={t("Utgivningsscheman")}
              value="schedules"
            />
            <Bananas.Tab
              icon={<PaidOutlinedIcon />}
              label={t("Priser")}
              value="prices"
            />
          </Bananas.Tabs>
        </Bananas.Header>
        {(activeTab === "titles" || activeTab == null) &&
          (publications != null && publications.length !== 0 ? (
            <Bananas.Content layout="fullWidth">
              <PublicationsList publications={publications} />
            </Bananas.Content>
          ) : (
            <ErrorCard
              errorMessage={t("Hittade inga titlar för ditt urval.")}
            />
          ))}

        {activeTab === "schedules" && (
          <>
            {schedules != null && schedules.length !== 0 ? (
              <Bananas.Content layout="fullWidth">
                {schedules.map((schedule) => (
                  <ScheduleCard
                    key={schedule.group_id}
                    refresh={refresh}
                    schedule={schedule}
                  />
                ))}
              </Bananas.Content>
            ) : (
              <ErrorCard
                errorMessage={t("Hittade inga titlar för ditt urval.")}
              />
            )}
            <SchedulesActionBar refresh={refresh} />
          </>
        )}
        {activeTab === "prices" && (
          <>
            <Bananas.Content layout="fullWidth">
              {prices != null && prices.length > 0 ? (
                sortedPrices.map((price) => (
                  <PriceListCard
                    key={price.data.group_id}
                    data={price.data}
                    defaultExpanded={price.isNew}
                    initialAdding={price.isNew && !price.data.group_id}
                    initialEditing={price.isNew && !price.data.group_id}
                    publications={publications ?? []}
                    refresh={refresh}
                    onCopy={handleCopyPriceList}
                  />
                ))
              ) : (
                <ErrorCard
                  errorMessage={t("Hittade inga titlar för ditt urval.")}
                />
              )}
              <NewPriceListDialog
                open={isNewPriceListDialogOpen}
                onClose={() => setIsNewPriceListDialogOpen(false)}
                onSubmit={handleCreatePriceList}
              />
            </Bananas.Content>
            <Bananas.ActionBar>
              <Button
                variant="outlined"
                onClick={() => setIsNewPriceListDialogOpen(true)}
              >
                {t("Lägg till prislista")}
              </Button>
            </Bananas.ActionBar>
          </>
        )}
      </Bananas.ContentWrapperWithActionBar>
    </Bananas.Page>
  );
};

export default PublicationListPage;
